// ============================================================================
// PostingDetailPage — fiche affichage (E1d, F-306) — /affichages/:id
// ============================================================================
// Le PoC n'a PAS de route detail dediee (item de liste). DECISION MASTER : porter au
// standard maison AVEC fiche routee (breadcrumb « Affichages › {titre} » ->
// PostingSummaryHeader -> description -> taches -> metadonnees piece jointe ->
// actions statut/edition selon role). Cohere avec « toute liste -> drill-in »
// (E1b/E3a). Toutes les gardes sont SERVEUR (viewer.canWrite = affordance).
//
// BLOB (CHORE-GEST-BLOBS) : la piece jointe affiche ses METADONNEES (nom/taille/
// type) + un telechargement ACTIF via le PROXY SERVEUR (jamais un SAS, jamais un
// href nu — le Bearer ne voyage pas dans une navigation). FALLBACK CONSERVE :
// blob absent (migration pas encore lancee) ou storage non cable => 404/503 ->
// toast, aucun 500. « Retirer » remet la reference a NULL sans supprimer le blob.
// L'AJOUT de piece jointe reste differe (aucun upload cable).

import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  EmptyState, ConfirmDialog, QueryError, SkeletonLine, SkeletonCard, GS_COMPONENTS,
} from '@gs/ui';
import { ChevronRight, FileText, ListChecks, ShieldAlert } from 'lucide-react';
import { ApiForbiddenError, ApiServerError } from '@gs/api-client';
import { useAuth } from '@gs/auth';
import { useTranslation } from '../../i18n';
import {
  usePostingDetail, useUpdatePostingStatus, useRemovePostingAttachment, postingErrorCode,
  type PostingDetailResponse, type PostingStatus,
} from '../../hooks/usePostings';
import { downloadBlobAs } from '../../lib/blobs/objectUrl';
import { PostingEditPanel } from '../../components/affichages/PostingEditPanel';
import { PostingsForbidden } from './AffichagesPage';
import { PostingSummaryHeader } from './PostingSummaryHeader';
import { formatFileSize } from './format';

const KNOWN_ERRORS = ['invalid', 'forbidden', 'notfound', 'server', 'download'];

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3">
      <h3 className="text-body font-semibold text-slate-900 dark:text-slate-100">{title}</h3>
      {children}
    </section>
  );
}

// ─── Actions de statut (F-305, M-301) ────────────────────────────────────────

function StatusActions({ detail, onError }: { detail: PostingDetailResponse; onError: (code: string) => void }) {
  const { t } = useTranslation();
  const status = useUpdatePostingStatus(detail.posting.id);
  const current = detail.posting.status;

  // Cycle ouvert PoC : marquer pourvu (btnAction), annuler (btnDangerOutline),
  // re-ouvrir (btnSecondary) — jamais le bouton du statut courant.
  const actions: { label: string; status: PostingStatus; className: string }[] = [];
  if (current !== 'filled') actions.push({ label: t('gest:affichages.actions.markFilled'), status: 'filled', className: GS_COMPONENTS.btnAction });
  if (current !== 'cancelled') actions.push({ label: t('gest:affichages.actions.cancel'), status: 'cancelled', className: GS_COMPONENTS.btnDangerOutline });
  if (current !== 'open') actions.push({ label: t('gest:affichages.actions.reopen'), status: 'open', className: GS_COMPONENTS.btnSecondary });

  return (
    <div className="flex flex-wrap items-center gap-2">
      {actions.map((a) => (
        <button
          key={a.status}
          type="button"
          disabled={status.isPending}
          onClick={() => status.mutate(a.status, { onError: (err) => onError(postingErrorCode(err)) })}
          className={`${a.className} touch-manipulation`}
        >
          {a.label}
        </button>
      ))}
    </div>
  );
}

// ─── Bloc metadonnees piece jointe (blob Firebase non migre) ─────────────────

function AttachmentBlock({ detail, onError }: { detail: PostingDetailResponse; onError: (code: string) => void }) {
  const { t, i18n } = useTranslation();
  const remove = useRemovePostingAttachment(detail.posting.id);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const attachment = detail.posting.attachment;

  /** Telechargement via le proxy serveur — echec = code metier toaste (jamais un crash). */
  async function handleDownload(): Promise<void> {
    if (!attachment) return;
    setDownloading(true);
    try {
      const ok = await downloadBlobAs(
        `/api/postings/${detail.posting.id}/attachment/download`,
        attachment.fileName,
      );
      // Echec = blob absent (404), storage non cable (503) ou refus : un seul
      // message utilisateur — le detail vit dans les logs serveur (GA-21 : jamais
      // de code brut a l'ecran).
      if (!ok) onError('download');
    } finally {
      setDownloading(false);
    }
  }

  if (!attachment) return null;

  const size = formatFileSize(i18n.language, attachment.sizeBytes);

  return (
    <Section title={t('gest:affichages.sections.attachment')}>
      <div className={`${GS_COMPONENTS.card} flex flex-wrap items-center gap-3 p-3.5 dark:border-slate-700 dark:bg-slate-800`}>
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-300">
          <FileText size={18} aria-hidden="true" />
        </span>
        <span className="min-w-0 flex-1">
          <p className="truncate text-field font-medium text-slate-800 dark:text-slate-100">{attachment.fileName}</p>
          <p className="truncate text-meta text-slate-500 dark:text-slate-400">
            {[attachment.contentType, size].filter(Boolean).join(' · ') || t('gest:affichages.attachment.present')}
          </p>
        </span>
        {/* Telechargement ACTIF (CHORE-GEST-BLOBS) : proxy serveur sous Bearer —
            jamais une navigation vers l'URL d'app nue (elle n'emporte pas le
            token). FALLBACK CONSERVE : si le blob manque (migration non lancee)
            ou si le storage n'est pas cable, le serveur repond 404/503, le
            telechargement echoue proprement et l'ecran toaste — aucun 500. */}
        <button
          type="button"
          onClick={() => { void handleDownload(); }}
          disabled={downloading}
          className={`${GS_COMPONENTS.btnSecondary} touch-manipulation`}
        >
          {t('gest:affichages.attachment.download')}
        </button>
        {detail.viewer.canWrite ? (
          <button
            type="button"
            onClick={() => setConfirmOpen(true)}
            disabled={remove.isPending}
            className={GS_COMPONENTS.btnDangerOutline}
          >
            {t('gest:affichages.attachment.remove')}
          </button>
        ) : null}
      </div>
      <ConfirmDialog
        open={confirmOpen}
        title={t('gest:affichages.attachment.confirmTitle')}
        message={t('gest:affichages.attachment.confirmMessage', { name: attachment.fileName })}
        confirmLabel={t('gest:affichages.attachment.confirmConfirm')}
        cancelLabel={t('gest:affichages.attachment.confirmCancel')}
        variant="danger"
        isLoading={remove.isPending}
        onConfirm={() =>
          remove.mutate(undefined, {
            onSuccess: () => setConfirmOpen(false),
            onError: (err) => { setConfirmOpen(false); onError(postingErrorCode(err)); },
          })
        }
        onCancel={() => setConfirmOpen(false)}
      />
    </Section>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

function DetailSkeleton() {
  return (
    <div className="space-y-4">
      <SkeletonLine className="h-4 w-64" />
      <SkeletonCard />
      <SkeletonCard />
    </div>
  );
}

export default function PostingDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const detailQ = usePostingDetail(id, !!user);
  const errorCode = searchParams.get('error');

  function setError(code: string) {
    const next = new URLSearchParams(searchParams);
    next.set('error', code);
    setSearchParams(next);
  }

  const notFound = detailQ.isError && detailQ.error instanceof ApiServerError && detailQ.error.status === 404;
  const forbidden = detailQ.isError && detailQ.error instanceof ApiForbiddenError;

  return (
    <div className="space-y-5">
      {/* Fil d'Ariane LABELLE « Affichages › {titre} » — bloc canonique UI_STANDARDS §Pattern fiche. */}
      <nav aria-label={t('gest:affichages.breadcrumbLabel')} className="mb-2 flex items-center gap-1.5 text-meta text-slate-500">
        <Link to="/affichages" className="touch-manipulation font-medium text-slate-500 transition-colors hover:text-slate-700 dark:hover:text-slate-300">
          {t('gest:affichages.title')}
        </Link>
        <ChevronRight size={14} aria-hidden className="text-slate-300 dark:text-slate-600" />
        <span aria-current="page" className="truncate font-semibold text-slate-800 dark:text-slate-100">
          {detailQ.data?.posting.title ?? '…'}
        </span>
      </nav>

      {errorCode ? (
        <div role="alert" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-field text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">
          {t(`gest:affichages.errors.${KNOWN_ERRORS.includes(errorCode) ? errorCode : 'server'}`)}
        </div>
      ) : null}

      {detailQ.isLoading ? (
        <DetailSkeleton />
      ) : notFound ? (
        <EmptyState
          icon={<ShieldAlert size={48} aria-hidden="true" />}
          title={t('gest:affichages.notFound.title')}
          description={t('gest:affichages.notFound.description')}
          action={<Link to="/affichages" className={GS_COMPONENTS.btnSecondary}>{t('gest:affichages.notFound.back')}</Link>}
        />
      ) : forbidden ? (
        <PostingsForbidden />
      ) : detailQ.isError ? (
        <QueryError message={t('gest:affichages.loadError')} onRetry={() => void detailQ.refetch()} />
      ) : detailQ.data ? (
        <>
          <PostingSummaryHeader posting={detailQ.data.posting} />

          {detailQ.data.viewer.canWrite ? (
            <>
              <StatusActions detail={detailQ.data} onError={setError} />
              <PostingEditPanel posting={detailQ.data.posting} onError={setError} />
            </>
          ) : null}

          <Section title={t('gest:affichages.sections.description')}>
            {detailQ.data.posting.description ? (
              <p className="whitespace-pre-wrap text-field text-slate-700 dark:text-slate-300">
                {detailQ.data.posting.description}
              </p>
            ) : (
              <p className="text-field text-slate-400 dark:text-slate-500">{t('gest:affichages.noDescription')}</p>
            )}
          </Section>

          <Section title={t('gest:affichages.sections.tasks')}>
            {detailQ.data.posting.tasks.length === 0 ? (
              <EmptyState icon={<ListChecks size={40} aria-hidden="true" />} title={t('gest:affichages.noTasks')} />
            ) : (
              <ul className="list-disc space-y-1 pl-5 text-field text-slate-700 dark:text-slate-300">
                {detailQ.data.posting.tasks.map((task, i) => (
                  <li key={i}>{task}</li>
                ))}
              </ul>
            )}
          </Section>

          <AttachmentBlock detail={detailQ.data} onError={setError} />
        </>
      ) : null}
    </div>
  );
}
